import React, { useCallback, useEffect } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { toast, Bounce } from "react-toastify"
import ProjectSessionDetail from "../ProjectSessionDetail"
import SessionDetailMini from "./SessionDetailMini"
import { ExitSessionModal, SessionCompletedModal } from "../ProjectSessions"
import useSessionDetail from "../../../hooks/useSessionDetail"
import {
  clearSessionReturnTo,
  resolveSessionCompletedRedirect,
} from "../../../helpers/sessionReturnTo"

/**
 * Session-specific content for the global floating player.
 * Stays mounted across full ↔ minimized so timer and table state persist.
 */
const SessionDetailPlayer = ({
  payload,
  mode,
  onMinimize,
  onRestore,
  onClose,
  onEnded,
  onActiveChange,
  onGuardModalChange,
}) => {
  const sessionId = payload?.sessionId
  const project = payload?.project || null
  const city = payload?.city || null
  const detail = useSessionDetail(sessionId, { project, city })
  const { onExit } = detail
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!detail.notFound) return undefined
    toast.error("Session not found.", {
      position: "bottom-center",
      theme: "colored",
      transition: Bounce,
    })
    onEnded?.()
    return undefined
  }, [detail.notFound, onEnded])

  useEffect(() => {
    if (detail.isLoading) return undefined

    if (detail.notFound || !detail.session) {
      return undefined
    }

    const session = detail.session
    const isActive =
      Boolean(session.id) &&
      session.canExit &&
      String(session.status || "active").toLowerCase() !== "ended"

    onActiveChange?.({
      sessionId: isActive ? session.id : null,
      isRunning: Boolean(session.isRunning),
    })
    return undefined
  }, [
    detail.isLoading,
    detail.notFound,
    detail.session,
    onActiveChange,
  ])

  const handleLeaveSession = async leavePayload => {
    const left = await detail.onLeaveSession(leavePayload)
    if (left) onActiveChange?.({ sessionId: null })
  }

  const handleExitClick = useCallback(
    event => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      onExit()
    },
    [onExit],
  )

  const handleRestore = useCallback(
    event => {
      event?.stopPropagation?.()
      if (detail.isExitOpen || detail.isCompletedOpen) return
      if (document.querySelector(".modal.show")) return
      onRestore?.()
    },
    [detail.isCompletedOpen, detail.isExitOpen, onRestore],
  )

  const resolvedProjectId =
    payload?.projectId ||
    project?.id ||
    detail.session?.projectId ||
    null
  const resolvedCityId =
    payload?.cityId || city?.id || detail.session?.city?.id || null
  // Prefer project when both exist — session collaborators must be project members.
  const isCitySession =
    Boolean(detail.session?.isCitySession) ||
    (Boolean(resolvedCityId) && !resolvedProjectId)

  const resolvedProjectName =
    project?.title ||
    project?.name ||
    detail.session?.projectName ||
    detail.session?.project?.name ||
    detail.session?.project?.title ||
    ""

  const projectForModal = isCitySession
    ? null
    : {
        id: resolvedProjectId || project?.id || null,
        title: resolvedProjectName || "—",
        name: resolvedProjectName || "—",
      }

  const cityForModal = isCitySession
    ? city ||
      detail.session?.city ||
      (detail.session?.cityName
        ? { name: detail.session.cityName, id: payload?.cityId }
        : null)
    : null

  const completedRedirect = resolveSessionCompletedRedirect({
    returnTo: payload?.returnTo,
    returnLabel: payload?.returnLabel,
    sessionId,
    isCitySession,
    cityId: resolvedCityId || cityForModal?.id || null,
    projectId: resolvedProjectId,
    pathname: location.pathname,
  })

  const handleCompletedClose = useCallback(() => {
    detail.setIsCompletedOpen(false)
    clearSessionReturnTo(sessionId)
    onEnded?.()

    if (completedRedirect?.path) {
      navigate(completedRedirect.path, { replace: true })
    }
  }, [detail, onEnded, completedRedirect, sessionId, navigate])

  useEffect(() => {
    const isGuardOpen = detail.isExitOpen || detail.isCompletedOpen
    onGuardModalChange?.(isGuardOpen)
    if (!isGuardOpen) return undefined
    document.body.classList.add("session-guard-modal-open")
    return () => {
      onGuardModalChange?.(false)
      document.body.classList.remove("session-guard-modal-open")
    }
  }, [detail.isCompletedOpen, detail.isExitOpen, onGuardModalChange])

  const sessionModals =
    typeof document === "undefined"
      ? null
      : createPortal(
          <div onClick={event => event.stopPropagation()}>
            <ExitSessionModal
              isOpen={detail.isExitOpen}
              onClose={() => {
                if (!detail.isLeaving) detail.setIsExitOpen(false)
              }}
              project={isCitySession ? null : projectForModal}
              city={isCitySession ? cityForModal : null}
              onFinish={handleLeaveSession}
              isSubmitting={detail.isLeaving}
            />
            <SessionCompletedModal
              isOpen={detail.isCompletedOpen}
              onClose={handleCompletedClose}
              time={detail.completedStats.time}
              xp={detail.completedStats.xp}
              redirectHint={
                completedRedirect?.label ||
                (isCitySession
                  ? "Returning to Shadow Working Events..."
                  : "Redirecting to sessions...")
              }
            />
          </div>,
          document.body,
        )

  return (
    <>
      {mode === "minimized" ? (
        <SessionDetailMini
          session={detail.session}
          timer={detail.displayTimer}
          isTimerUpdating={detail.isTimerUpdating}
          onTimerToggle={detail.onTimerToggle}
          onRestore={handleRestore}
          onExit={handleExitClick}
        />
      ) : (
        <ProjectSessionDetail
          session={detail.session}
          timer={detail.displayTimer}
          isLoading={detail.isLoading}
          error={detail.error}
          members={detail.members}
          gallery={detail.gallery}
          membersTotal={detail.membersTotal}
          membersPage={detail.membersPage}
          membersPageSize={detail.membersPageSize}
          isMembersLoading={detail.isMembersLoading}
          membersError={detail.membersError}
          onMembersPageChange={detail.onMembersPageChange}
          searchQuery={detail.searchQuery}
          onSearchChange={detail.onSearchChange}
          statusFilters={detail.statusFilters}
          onStatusFiltersChange={detail.onStatusFiltersChange}
          sortBy={detail.sortBy}
          onSortByChange={detail.onSortByChange}
          sortDirection={detail.sortDirection}
          onSortDirectionChange={detail.onSortDirectionChange}
          onExit={handleExitClick}
          onTimerToggle={detail.onTimerToggle}
          isTimerUpdating={detail.isTimerUpdating}
          onMinimize={onMinimize}
          onClose={onClose}
          projectId={isCitySession ? null : resolvedProjectId}
          project={isCitySession ? null : project}
          cityId={isCitySession ? resolvedCityId : null}
          currentUserId={detail.currentUserId}
          existingCollaboratorUserIds={detail.existingCollaboratorUserIds}
          onAddCollaborators={detail.onAddCollaborators}
          onRemoveCollaborator={detail.onRemoveCollaborator}
          isMutatingCollaborators={detail.isMutatingCollaborators}
          impressions={detail.impressions}
          reactingMemberId={detail.reactingMemberId}
          onToggleImpression={detail.onToggleImpression}
        />
      )}
      {sessionModals}
    </>
  )
}

export default SessionDetailPlayer
